import { useLayerStore } from '@/store/use-layer-store';
import { EventType } from '@/utils/emitter.utils';
import { onBeforeUnmount, onDeactivated, reactive, ref, watch } from 'vue';
import { useEmitterListener } from './use-event-listener';
import { debounce } from 'lodash-es';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { Description } from '@/types/watchface';
import {
  useRouteState,
  useWatchfaceSave,
  watchfaceImageLoading,
  watchfaceOpening
} from '.';

type Status = 'saved' | 'unsaved' | 'saving';
export const status = ref<Status>();

/**
 * 自动保存时间：minutes
 */
const autoSave = ref<number>(2);
const _timer = ref<number>();

export const useWatchfaceStatus = () => {
  const layerStore = useLayerStore();
  const watchfaceStore = useWatchfaceStore();
  const { saveWatchfaceData } = useWatchfaceSave();
  const { isEditorPage } = useRouteState();

  const setStatus = (val: Status) => {
    status.value = val;
  };

  // 当为空时，代表首次打开
  const _lastStore = reactive({
    layer: '',
    desc: ''
  });

  const resetLastStore = (data?: {
    themes?: StoreThemeItem[];
    description?: Description;
  }) => {
    const { themes, description } = data ?? {};
    try {
      _lastStore.layer = JSON.stringify(themes ?? layerStore.$state.themes);
    } catch (error) {
      _lastStore.layer = '';
    }
    try {
      _lastStore.desc = JSON.stringify(
        description ?? watchfaceStore.$state.description
      );
    } catch (error) {
      _lastStore.desc = '';
    }
  };

  const _setUnsavedOnChange = debounce(() => {
    const _layer = JSON.stringify(layerStore.$state.themes);
    const _desc = JSON.stringify(watchfaceStore.$state.description);

    const { layer, desc } = _lastStore;

    if (_layer == layer && _desc == desc) {
      status.value = 'saved';
    } else {
      status.value = 'unsaved';
    }
  }, 50);

  // 监听数据变动，设置状态为未保存
  [layerStore, watchfaceStore].forEach((store) => {
    store.$subscribe(() => {
      if (watchfaceOpening.value || watchfaceImageLoading.value) return;
      if (!isEditorPage.value) return;
      _setUnsavedOnChange();
    });
  });

  useEmitterListener(
    [EventType['watchface:opened'], EventType['watchface:saved']],
    () => {
      status.value = 'saved';
      resetLastStore();
    }
  );

  const _closeAutoSave = () => {
    if (_timer.value) clearTimeout(_timer.value);
  };

  watch(status, (val) => {
    // 如果是已保存，则取消自动保存
    if (val == 'saved') {
      _closeAutoSave();
      // 获取本次保存的数据
      resetLastStore();
    }

    // 如果是未保存，则需要开启自动保存
    if (val == 'unsaved') {
      // clear previous
      _closeAutoSave();
      // set new timer
      _timer.value = window.setTimeout(() => {
        // 发送自动保存事件
        saveWatchfaceData();
      }, autoSave.value * 60 * 1000);
    }
  });

  useEmitterListener(
    EventType['watchface:description-saved'],
    (desc: Description) => {
      resetLastStore({ description: desc });
    }
  );

  onBeforeUnmount(() => {
    _closeAutoSave();
  });
  onDeactivated(() => {
    _closeAutoSave();
  });

  return {
    setStatus,
    resetLastStore
  };
};
